import {
	AfterViewChecked,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	inject,
	NgZone,
	OnDestroy,
	OnInit,
	TrackByFunction,
} from "@angular/core";
import {
	ActivatedRoute,
	Event,
	NavigationCancel,
	NavigationEnd,
	NavigationError,
	NavigationStart,
	Router,
	RouterEvent,
	RouterLink,
} from "@angular/router";
import { ListService } from "common/list/list.service";
import { MultiLevelSelectionService } from "common/multi-level-selection.service";
import { trackByIndexFunction } from "core/functions";
import { MainService } from "core/main.service";
import { LanguageService } from "core/translate/language.service";
import { ActiveFloatingActionButtonType, ApplicationState, State } from "core/volatile-application-state.model";
import {
	ActionStateWrapper,
	BaseNavigationComponent,
	isFollowUpActionMenuItem,
	isRearrangeUIAction,
} from "navigation/list/base/base-navigation.component";
import { NavigationConfiguration } from "navigation/list/navigation-configuration.interface";
import { ListNavigationEntry } from "navigation/list/navigation-entry.interface";
import { FollowUpAction, MenuUIAction, NavigationMenuItem, NavMode } from "navigation/list/navigation.interface";
import { NavigationService } from "navigation/list/navigation.service";
import { combineLatest, Observable, of, Subject, Subscription, timer } from "rxjs";
import { filter, map, switchMap, take, takeUntil, tap } from "rxjs/operators";
import { NavigationRearrangeListService } from "navigation/list/navigation-rearrange-list.service";
import { AsyncPipe, NgClass, NgFor, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault } from "@angular/common";
import { MenuComponent } from "common/menu/menu.component";
import { TranslateModule } from "@ngx-translate/core";
import { SingleLineNavigationEntryComponent } from "./entry/single-line-navigation-entry.component";
import { TwoLineNavigationEntryComponent } from "./entry/two-line-navigation-entry.component";
import { TwoLineSplitRatingNavigationEntryComponent } from "./entry/two-line-split-rating-navigation-entry.component";
import { ChangeMarkerNavigationEntryComponent } from "./entry/change-marker-navigation-entry.component";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { TrimDirective } from "common/trim.directive";
import { RestrictInputDirective } from "common/form/restrict-input.directive";
import { AutofocusDirective } from "common/autofocus/autofocus.directive";
import { KeyUpDirective } from "common/key-up.directive";
import { SupportSectionComponent } from "navigation/support-section/support-section.component";
import { FloatingActionMenuComponent } from "common/floating-action-menu/floating-action-menu.component";
import { DisabledFilterPipe } from "navigation/disabled-filter.pipe";
import { DialogService } from "common/dialog/dialog.service";
import { HttpErrorResponse } from "@angular/common/http";
import { LayoutService } from "core/layout.service";

@Component({
	selector: "stages-navigation",
	templateUrl: "./navigation.component.html",
	styleUrls: ["./navigation.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		NgIf,
		RouterLink,
		MenuComponent,
		NgFor,
		TranslateModule,
		NgClass,
		NgSwitch,
		NgSwitchCase,
		SingleLineNavigationEntryComponent,
		TwoLineNavigationEntryComponent,
		TwoLineSplitRatingNavigationEntryComponent,
		ChangeMarkerNavigationEntryComponent,
		NgSwitchDefault,
		ReactiveFormsModule,
		FormsModule,
		TrimDirective,
		RestrictInputDirective,
		AutofocusDirective,
		KeyUpDirective,
		SupportSectionComponent,
		FloatingActionMenuComponent,
		AsyncPipe,
		DisabledFilterPipe,
	],
})
export class NavigationComponent extends BaseNavigationComponent implements AfterViewChecked, OnInit, OnDestroy {
	private languageService = inject(LanguageService);
	private navigationService = inject(NavigationService);
	private navigationRearrangeListService = inject(NavigationRearrangeListService);
	mainService = inject(MainService);
	private zone = inject(NgZone);
	private listService = inject(ListService);
	private cd = inject(ChangeDetectorRef);
	private dialogService = inject(DialogService);
	private layoutService = inject(LayoutService);

	applicationState: ApplicationState;

	navigationConfiguration$!: Observable<NavigationConfiguration>;
	workspaceView$!: Observable<stages.workspace.application.WorkspaceView>;
	newNavigationConfiguration?: NavigationConfiguration;
	focusedEntry?: ListNavigationEntry;
	showFloatingActionButton$ = new Observable<boolean>();
	isFloatingActionButtonAllowed$ = new Observable<boolean>();
	isFloatingActionButtonAllowedByNavigationLayer$ = new Observable<boolean>();

	showAddButton = false;
	trackByIndex = trackByIndexFunction;

	rearrangeOpts: JQueryUI.SortableOptions = {
		axis: "y",
		handle: ".draggableAreaForSort",
		disabled: false,
		// typescript errors are ignored for the following statement,
		// since our placeholder option is non-conformant to the jquery-ui api
		// @ts-expect-error
		placeholder: {
			element: (currentItem: JQuery) => {
				return $('<li class="list-item ghost"><div class="label"></div></li>')[0];
			},
			update: () => {},
		},

		start: (event: JQueryEventObject, ui: JQueryUI.SortableUIParams) => {
			ui.item.data("startIndex", ui.item.index());
		},

		update: (event: JQueryEventObject, ui: JQueryUI.SortableUIParams) => {
			this.zone.run(() => {
				this.navigationRearrangeListService.moveNavigationEntry(ui.item.data("startIndex"), ui.item.index());
				this.rearrangeOrder = "custom";
				this.cd.detectChanges();
			});
		},
		animation: 300,
	};

	alphabeticalOrderToggle?: boolean;

	lastAction!: string;
	lastFolderId?: string;

	singleLine = "single-line";
	twoLine = "two-line";
	twoLineSplitRating = "two-line-split-rating";
	changeMarker = "change-marker";

	private destroy$ = new Subject<boolean>();
	private cancelProgressIndicatorTimer$ = new Subject<boolean>();
	private navigationContentChange$?: Subscription;
	private editInProgress: boolean = false;
	private isMobileView: boolean = false;
	private ignoreBlur = false;

	constructor() {
		const mainService = inject(MainService);
		const route = inject(ActivatedRoute);
		const router = inject(Router);
		const selectionService = inject<MultiLevelSelectionService<ListNavigationEntry>>(MultiLevelSelectionService);

		super(route, router, selectionService);

		this.applicationState = mainService.applicationState;
	}

	ngOnInit(): void {
		this.router.events
			.pipe(
				filter((e: Event | RouterEvent): e is RouterEvent => e instanceof RouterEvent),
				takeUntil(this.destroy$),
			)
			.subscribe((e: RouterEvent) => {
				this.handleProgressbar(e);
			});

		this.navigationConfiguration$ = this.navigationService.navigationConfiguration$.pipe(
			map((navigationConfiguration) => {
				this.newNavigationConfiguration = navigationConfiguration;
				const actionParam = this.getActionParam(navigationConfiguration);
				if (actionParam === "rearrange") {
					this.rearrangeOrder = this.determineCurrentSortOrder(navigationConfiguration.list);
					if (this.rearrangeOrder === "alphabetical") {
						// copying array is necessary (see test report of #34549)
						const copiedList = navigationConfiguration.list.slice();
						this.preliminaryAlphabeticalSorting(copiedList);
						navigationConfiguration.list = copiedList;
					}
				}
				if (actionParam !== this.lastAction) {
					this.focusedEntry = undefined;
				}
				this.resetSelectionAfterAction(actionParam, navigationConfiguration);

				return navigationConfiguration;
			}),
		);
		this.workspaceView$ = this.mainService.workspaceView$;

		this.navigationService.forceRefresh$
			.pipe(
				switchMap((forceRefresh) => {
					if (!forceRefresh) {
						return of(undefined);
					}
					return this.navigationService.navigationConfiguration$.pipe(take(1));
				}),
				takeUntil(this.destroy$),
			)
			.subscribe((conf) => {
				if (conf?.activatedRoute && conf.baseRoute) {
					this.navigate(new FollowUpAction(NavMode.SELF).withForcedReload(), conf);
					this.cd.detectChanges();
				}
			});

		this.layoutService.breakpoint.pipe(takeUntil(this.destroy$)).subscribe((breakpoint) => {
			this.isMobileView = breakpoint.isMobile;
		});

		this.isFloatingActionButtonAllowed$ = this.mainService.applicationState.activeFloatingActionButtonType.pipe(
			map((activeButton) => {
				const isAllowed = this.mainService.isFloatingActionButtonActivationAllowed(
					activeButton,
					ActiveFloatingActionButtonType.NAVDRAWER,
				);
				if (isAllowed && activeButton !== ActiveFloatingActionButtonType.NAVDRAWER) {
					this.mainService.applicationState.activeFloatingActionButtonType.next(
						ActiveFloatingActionButtonType.NAVDRAWER,
					);
				}
				return isAllowed;
			}),
			takeUntil(this.destroy$),
		);

		this.isFloatingActionButtonAllowedByNavigationLayer$ = this.navigationConfiguration$.pipe(
			map((navigationConfiguration) => {
				const folder = navigationConfiguration.folder;
				const actionButtonSpec = navigationConfiguration.floatingActionButton;
				return (
					actionButtonSpec !== undefined &&
					actionButtonSpec.type === "withMenu" &&
					actionButtonSpec.menuItems!.length > 0 &&
					folder !== undefined &&
					folder.actions.CREATE_CHILD
				);
			}),
			tap((isFloatingActionButtonAllowedByNavigationLayer) => {
				if (!isFloatingActionButtonAllowedByNavigationLayer) {
					this.applicationState.activeFloatingActionButtonType.next(ActiveFloatingActionButtonType.NONE);
				}
			}),
			takeUntil(this.destroy$),
		);

		this.showFloatingActionButton$ = combineLatest([
			this.isFloatingActionButtonAllowed$,
			this.isFloatingActionButtonAllowedByNavigationLayer$,
		]).pipe(
			map(([isFloatingActionButtonAllowedByApplicationState, isFloatingActionButtonAllowedByNavigationLayer]) => {
				return isFloatingActionButtonAllowedByNavigationLayer && isFloatingActionButtonAllowedByApplicationState;
			}),
			takeUntil(this.destroy$),
		);
	}

	ngOnDestroy(): void {
		this.destroy$.next(true);
		this.destroy$.complete();
		if (this.navigationContentChange$) {
			this.navigationContentChange$.unsubscribe();
		}
	}

	ngAfterViewChecked(): void {
		if (this.newNavigationConfiguration) {
			const actionParam = this.getActionParam(this.newNavigationConfiguration);
			if (actionParam === "view") {
				if (this.focusedEntry) {
					scrollToEntry(this.focusedEntry, this.newNavigationConfiguration.folder);
				} else {
					this.scrollTo(this.newNavigationConfiguration);
				}
			}
			if (actionParam === "rearrange") {
				this.navigationRearrangeListService.rearrangeList = this.newNavigationConfiguration.list.slice();
				$(".stages-sortable").sortable(this.rearrangeOpts);
				this.cd.detectChanges();
			} else {
				// jquery-ui might have added the ui-sortable class
				$(".ui-sortable").sortable("destroy");
			}
			this.newNavigationConfiguration = undefined;
		}
	}

	private resetSelectionAfterAction(actionParam: string, navigationConfiguration: NavigationConfiguration): void {
		if (isViewAction(actionParam)) {
			if (!isViewAction(this.lastAction)) {
				this.resetSelection(navigationConfiguration.selectionIdentifier);
			} else if (
				this.lastFolderId !== undefined &&
				navigationConfiguration.folder !== undefined &&
				navigationConfiguration.folder.id !== this.lastFolderId
			) {
				this.resetSelection(navigationConfiguration.selectionIdentifier);
			}
		}
		this.lastAction = actionParam;
		this.lastFolderId = navigationConfiguration.folder === undefined ? undefined : navigationConfiguration.folder.id;
	}

	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: Remove this comment and fix warnings!
	trackByIdAndAlphabeticalOrderToggle: TrackByFunction<any> = (_index, navElement) => {
		if (this.navigationRearrangeListService.rearrangeList.length > 0) {
			return `${this.trackById(_index, navElement)}_${this.alphabeticalOrderToggle}`;
		}
		return this.trackById(_index, navElement);
	};

	closeNavigationIfLeaf(entry: ListNavigationEntry): void {
		if (!entry.hasChildren && this.isMobileView) {
			this.applicationState.navigationDrawerOpen = State.RESPONSIVE;
		}
	}

	private triggerProgressIndicatorTimer(): void {
		timer(1000)
			.pipe(takeUntil(this.cancelProgressIndicatorTimer$))
			.subscribe(() => {
				this.mainService.setNavigationInProgress(true);
				this.cd.detectChanges();
			});
	}

	scrollTo(navigationConfiguration: NavigationConfiguration): void {
		const folder = navigationConfiguration.folder;
		const self = navigationConfiguration.self;

		if (!folder || !self) {
			return;
		}

		scrollToEntry(self, folder);
	}

	scrollToNewChild(): void {
		const newChildElement = document.getElementById("new-child");
		if (newChildElement) {
			newChildElement.scrollIntoView();
		}
	}

	onOrderChange(rearrangeOrder: "alphabetical" | "custom", navigationConfiguration: NavigationConfiguration): void {
		this.rearrangeOrder = rearrangeOrder;
		if (this.rearrangeOrder === "alphabetical") {
			navigationConfiguration.list.forEach((entry) => {
				entry.sortKey = 0;
			});
			this.navigationService.update(navigationConfiguration);
			this.alphabeticalOrderToggle = !this.alphabeticalOrderToggle;
		}
	}

	determineCurrentSortOrder(entries: ListNavigationEntry[]): "alphabetical" | "custom" {
		return !entries || entries.length === 0 || entries[0].sortKey === 0 ? "alphabetical" : "custom";
	}

	preliminaryAlphabeticalSorting(entries: ListNavigationEntry[]): void {
		if (localeCompareSupported()) {
			entries.sort((a: ListNavigationEntry, b: ListNavigationEntry) => {
				return getValueForSort(a).localeCompare(getValueForSort(b), this.languageService.getCurrentLanguage());
			});
		} else {
			entries.sort(poorLocaleComparePolyfill);
		}
	}

	wrapMenuItems(
		context: ListNavigationEntry,
		menuItems: NavigationMenuItem[] | undefined,
		navigationConfiguration: NavigationConfiguration,
	): MenuItem[] {
		let convertedMenuItems: MenuItem[] = [];
		if (!menuItems) {
			return convertedMenuItems;
		}
		convertedMenuItems = menuItems.map((item: NavigationMenuItem) => {
			return isFollowUpActionMenuItem(item)
				? {
						name: item.name,
						nameParam: item.nameParam,
						iconClass: Array.isArray(item.iconClass) ? item.iconClass : [item.iconClass], // TODO Vllt Interface anpassen?!
						disabled: item.disabled,
						on: () => {
							let selection = null;
							if (item.storeElement) {
								selection = this.newSelection(navigationConfiguration);
								selection.select(context);
							}
							const action: MenuUIAction = {
								indicateProgress: item.indicateProgress,
								invalidate: item.invalidate,
								async onMenuClick(): Promise<FollowUpAction> {
									return item.onDoFollowUp(context, navigationConfiguration.folder!);
								},
								self: navigationConfiguration.self!,
								folder: navigationConfiguration.folder!,
							};
							this.processFollowUpAction(
								action,
								navigationConfiguration.activatedRoute!,
								navigationConfiguration.baseRoute!,
								selection,
								() => navigationConfiguration.invalidate(),
							);
						},
					}
				: item;
		});
		return convertedMenuItems;
	}

	// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- TODO: Remove this comment and fix warnings!
	hasToolbar(navigationConfiguration: NavigationConfiguration): {} {
		return {
			"has-toolbar":
				navigationConfiguration.homeRoute !== undefined || navigationConfiguration.parentRoute !== undefined,
		};
	}

	isMainMenuEnabled(navigationConfiguration: NavigationConfiguration): boolean {
		const actionName = this.getActionParam(navigationConfiguration);
		const navigationMode = this.getCurrentMode(navigationConfiguration.supportedModes, actionName);
		return navigationMode.isMenuEnabled;
	}

	getCurrentActions(navigationConfiguration: NavigationConfiguration): ActionStateWrapper[] {
		const actionName = this.getActionParam(navigationConfiguration);
		const result: Array<ActionStateWrapper> = [];
		const currentMode = this.getCurrentMode(navigationConfiguration.supportedModes, actionName);
		if (currentMode.uiActions.length > 0) {
			const selection = this.getSelection(navigationConfiguration, currentMode.isSelectEnabled);
			for (const a of currentMode.uiActions) {
				if (isRearrangeUIAction(a)) {
					a.getSortedEntries = () => {
						return this.navigationRearrangeListService.rearrangeList;
					};
				}
				result.push(
					new ActionStateWrapper(
						a,
						this,
						selection,
						navigationConfiguration.activatedRoute!,
						navigationConfiguration.baseRoute!,
						() => navigationConfiguration.invalidate(),
					),
				);
			}
		}
		return result;
	}

	private async confirmCreationOfElementWithSameName(name: string): Promise<boolean> {
		return this.dialogService.confirm(
			"search.autoComplete.assocTarget.sameNameExistsCreateConfirm",
			{ name },
			"create",
			"cancel",
			false,
		);
	}

	async onEditInput($event: KeyboardEvent, entry: ListNavigationEntry, singleEdit: boolean): Promise<void> {
		const inputElement = $event.target as HTMLInputElement;
		const currentInput = inputElement.value;

		if ($event.key === "Escape" && !this.ignoreBlur) {
			inputElement.blur();
			return;
		}
		if ($event.key !== "Enter" || !currentInput || currentInput.trim() === "" || this.editInProgress) {
			return;
		}
		if (!entry.editDone) {
			console.log("no editDone callback defined for " + JSON.stringify(entry));
			return;
		}

		this.mainService.setNavigationInProgress(true);
		this.editInProgress = true;
		inputElement.readOnly = true;
		let confirmed = true;
		try {
			await entry.editDone(currentInput, false);
		} catch (err: unknown) {
			if (err instanceof HttpErrorResponse && err.status === 409) {
				confirmed = await this.handleEditConflict(inputElement, entry);
			} else {
				throw err;
			}
		} finally {
			this.mainService.setNavigationInProgress(false);
			this.editInProgress = false;
			if (singleEdit) {
				inputElement.blur();
			} else {
				if (confirmed) {
					inputElement.value = "";
				}
				inputElement.readOnly = false;
				this.scrollToNewChild();
			}
		}
	}

	private async handleEditConflict(inputElement: HTMLInputElement, entry: ListNavigationEntry): Promise<boolean> {
		const currentInput = inputElement.value;
		this.ignoreBlur = true;
		const confirmed = await this.confirmCreationOfElementWithSameName(currentInput);
		if (confirmed) {
			await entry.editDone(currentInput, true);
		}
		inputElement.focus();
		this.ignoreBlur = false;
		return confirmed;
	}

	stopEditEntry(
		entry: ListNavigationEntry,
		navigationConfiguration: NavigationConfiguration,
		mouseEvent: MouseEvent,
	): void {
		if (this.ignoreBlur) {
			return;
		}
		entry.displayMode = "VIEW";
		this.focusedEntry = entry;
		if (this.isInternalLinkClicked(mouseEvent)) {
			return;
		}
		this.navigate(new FollowUpAction(NavMode.SELF).withForcedReload(), navigationConfiguration);
	}

	isInternalLinkClicked(mouseEvent: MouseEvent): boolean {
		return (
			!!mouseEvent.relatedTarget &&
			mouseEvent.relatedTarget instanceof HTMLAnchorElement &&
			!this.isExternalLinkClicked(mouseEvent.relatedTarget)
		);
	}

	isExternalLinkClicked(clickedElement: HTMLAnchorElement): boolean {
		return clickedElement.target === "_blank" && !!clickedElement.href;
	}

	isSimpleFloatingActionButtonVisible(navigationConfiguration: NavigationConfiguration): boolean {
		const folder = navigationConfiguration.folder;
		const actionButtonSpec = navigationConfiguration.floatingActionButton;
		return (
			actionButtonSpec !== undefined &&
			actionButtonSpec.type === "simple" &&
			folder !== undefined &&
			folder.actions.CREATE_CHILD
		);
	}

	onFloatingActionButtonClicked(subtypeIdent: string, navigationConfiguration: NavigationConfiguration): void {
		this.navigate(new FollowUpAction(NavMode.SELF, "add").withSubtype(subtypeIdent), navigationConfiguration);
	}

	private async navigate(
		followUpAction: FollowUpAction,
		navigationConfiguration: NavigationConfiguration,
	): Promise<boolean> {
		const action: MenuUIAction = {
			indicateProgress: false,
			invalidate: false,

			// eslint-disable-next-line @typescript-eslint/require-await -- TODO: Remove this comment and fix warnings!
			async onMenuClick(): Promise<FollowUpAction> {
				return followUpAction;
			},
			self: navigationConfiguration.self!,
			folder: navigationConfiguration.folder!,
		};
		return this.processFollowUpAction(
			action,
			navigationConfiguration.activatedRoute!,
			navigationConfiguration.baseRoute!,
			null,
			() => navigationConfiguration.invalidate(),
		);
	}

	isSingleLineDisplayed(displayType: string): boolean {
		switch (displayType) {
			case this.singleLine: {
				return true;
			}
			case this.twoLine: {
				return false;
			}
			case this.twoLineSplitRating: {
				return false;
			}
			case this.changeMarker: {
				return false;
			}
			default: {
				return true;
			}
		}
	}

	getVisibleNavigationEntries(completeList: ListNavigationEntry[]): ListNavigationEntry[] {
		let addEntry;
		if (completeList.length > 0 && completeList[completeList.length - 1].displayMode === "ADD") {
			addEntry = completeList[completeList.length - 1];
		}
		const visibleNavigationEntries = this.listService.getVisibleListItems(completeList) as ListNavigationEntry[];
		if (addEntry && visibleNavigationEntries[visibleNavigationEntries.length - 1].displayMode !== "ADD") {
			visibleNavigationEntries.push(addEntry);
		}
		return visibleNavigationEntries;
	}

	hasOmmittedEntries(completeList: ListNavigationEntry[]): boolean {
		return this.listService.hasOmmittedItems(completeList);
	}

	handleProgressbar(event: RouterEvent): void {
		if (event instanceof NavigationStart) {
			this.triggerProgressIndicatorTimer();
		}
		if (event instanceof NavigationEnd) {
			this.cancelProgressIndicator();
		}

		// Set loading state to false in both of the below events to hide the ProgressIndicator in case a request fails
		if (event instanceof NavigationCancel) {
			this.cancelProgressIndicator();
		}
		if (event instanceof NavigationError) {
			this.cancelProgressIndicator();
		}
	}

	getMenuItemsForFloatingActionButton(navigationConfiguration: NavigationConfiguration): MenuItem[] {
		const menuItemsOutput: MenuItem[] = [];
		if (navigationConfiguration.floatingActionButton?.type === "simple") {
			menuItemsOutput.push({
				on: () => {
					this.onFloatingActionButtonClicked("", navigationConfiguration);
				},
			});
			return menuItemsOutput;
		}

		const menuItemsInput = navigationConfiguration?.floatingActionButton?.menuItems;
		if (menuItemsInput) {
			for (const item of menuItemsInput) {
				menuItemsOutput.push({
					on: () => {
						this.onFloatingActionButtonClicked(item.subtypeIdent, navigationConfiguration);
					},
					translated: item.nameTranslate,
					name: item.name,
					iconClass: item.iconClass,
					messageKey: item.nameTranslate,
				});
			}
		}

		return menuItemsOutput;
	}

	private cancelProgressIndicator(): void {
		this.mainService.setNavigationInProgress(false);
		this.cancelProgressIndicatorTimer$.next(true);
	}
}

function isViewAction(action: string | null): boolean {
	return action === null || action === "view";
}

function scrollToEntry(entry: ListNavigationEntry, folder?: ListNavigationEntry): void {
	const container: Element | null = document.querySelector(".nav-drawer nav");
	if (!container) {
		return;
	}

	if (folder && folder.id === entry.id) {
		container.scrollTop = 0;
		return;
	}

	/*
	 * The selected element is not a folder, so we need to decide if we have to scroll or not.
	 */
	const item: HTMLElement | null = document.getElementById("navItem" + entry.id);
	if (!item) {
		return;
	}
	const contRect = container.getBoundingClientRect();
	const itemRect = item.getBoundingClientRect();
	if (itemRect.top < contRect.top || itemRect.bottom > contRect.bottom) {
		item.scrollIntoView();
	}
}

function localeCompareSupported(): boolean {
	return !!"foo".localeCompare;
}

function isLowerCase(s: string): boolean {
	if (!s) {
		return false;
	}
	return s.startsWith(s[0].toLowerCase());
}

function getValueForSort(entry: ListNavigationEntry): string {
	if (!entry.getValueForAlphabeticalSort) {
		return "";
	}
	return entry.getValueForAlphabeticalSort();
}

function poorLocaleComparePolyfill(a: ListNavigationEntry, b: ListNavigationEntry): number {
	const valueA = getValueForSort(a);
	const valueB = getValueForSort(b);
	const aIsLowerCase = isLowerCase(valueA);
	const bIsLowerCase = isLowerCase(valueB);
	if (aIsLowerCase && !bIsLowerCase) {
		return -1;
	}
	if (!aIsLowerCase && bIsLowerCase) {
		return 1;
	}
	if (valueA < valueB) {
		return -1;
	} else if (valueA > valueB) {
		return 1;
	}
	return 0;
}
